/**
 * 세션 스토리지 접근 중앙화
 * JSON 파싱/직렬화를 자동으로 처리하고 에러 핸들링을 포함
 */

/**
 * @param {string} key - 저장된 키
 * @param {*} defaultValue - 키가 없을 때 반환할 기본값
 * @returns {*} 저장된 값 또는 기본값
 */
export const getStorage = (key, defaultValue = null) => {
  try {
    const item = window.sessionStorage.getItem(key);
    if (item === null) {
      return defaultValue;
    }
    // JSON 파싱 시도
    try {
      return JSON.parse(item);
    } catch (e) {
      // JSON이 아닌 경우 원본 문자열 반환
      return item;
    }
  } catch (e) {
    if (process.env.NODE_ENV === "development") {
      console.error(`Storage get error for key ${key}:`, e);
    }
    return defaultValue;
  }
};

/**
 * @param {string} key - 저장할 키
 * @param {*} value - 저장할 값 (자동으로 JSON 직렬화)
 */
export const setStorage = (key, value) => {
  try {
    if (value === null || value === undefined) {
      window.sessionStorage.removeItem(key);
      return;
    }
    // 문자열이 아닌 경우 JSON 직렬화
    const serializedValue =
      typeof value === "string" ? value : JSON.stringify(value);
    window.sessionStorage.setItem(key, serializedValue);
  } catch (e) {
    if (process.env.NODE_ENV === "development") {
      console.error(`Storage set error for key ${key}:`, e);
    }
  }
};

/**
 * @param {string} key - 제거할 키
 */
export const removeStorage = (key) => {
  try {
    window.sessionStorage.removeItem(key);
  } catch (e) {
    if (process.env.NODE_ENV === "development") {
      console.error(`Storage remove error for key ${key}:`, e);
    }
  }
};

/**
 * 세션 스토리지 클리어어
 */
export const clearStorage = () => {
  try {
    window.sessionStorage.clear();
  } catch (e) {
    if (process.env.NODE_ENV === "development") {
      console.error("Storage clear error:", e);
    }
  }
};

/**
 * 편의 메서드: 자주 사용되는 키들에 대한 getter/setter
 */
export const storage = {
  // 로그인 관련
  getUserId: () => getStorage("user_id"),
  setUserId: (value) => setStorage("user_id", value),
  
  getUserName: () => getStorage("user_name"),
  setUserName: (value) => setStorage("user_name", value),
  
  getWorkplaceCd: () => getStorage("workplace_cd"),
  setWorkplaceCd: (value) => setStorage("workplace_cd", value),
  
  getWorkplaceNm: () => getStorage("workplace_nm"),
  setWorkplaceNm: (value) => setStorage("workplace_nm", value),
  
  // 사용자 객체 반환
  getUser: () => ({
    id: getStorage("workplace_cd"), // CUST_CD (회사 코드)
    name: getStorage("workplace_nm"), // CUST_NAME (회사명)
    userId: getStorage("user_id"), // USER_ID (사용자 ID)
    userName: getStorage("user_name"), // USER_NAME (사용자명)
  }),
  
  // 프로그램 관련
  getProgram: () => getStorage("program", []),
  setProgram: (value) => setStorage("program", value),
  
  // 코드 관련
  getCodeNm: () => getStorage("code_nm", []),
  setCodeNm: (value) => setStorage("code_nm", value),
  
  getCodeStan: () => getStorage("code_stan", []),
  setCodeStan: (value) => setStorage("code_stan", value),
  
  getYardCustCd: () => getStorage("yardCustCd", []),
  setYardCustCd: (value) => setStorage("yardCustCd", value),
  
  getNameCd: () => getStorage("nameCd", []),
  setNameCd: (value) => setStorage("nameCd", value),
  
  getTypeCd: () => getStorage("typeCd", []),
  setTypeCd: (value) => setStorage("typeCd", value),
  
  getStanCd: () => getStorage("stanCd", []),
  setStanCd: (value) => setStorage("stanCd", value),
  
  getSellCustCd: () => getStorage("sellCustCd", []),
  setSellCustCd: (value) => setStorage("sellCustCd", value),
  
  getBuyCustCd: () => getStorage("buyCustCd", []),
  setBuyCustCd: (value) => setStorage("buyCustCd", value),
  
  getSalesManCd: () => getStorage("salesManCd", []),
  setSalesManCd: (value) => setStorage("salesManCd", value),
  
  getJobCustCd: () => getStorage("jobCustCd", []),
  setJobCustCd: (value) => setStorage("jobCustCd", value),
  
  getProcPlantCd: () => getStorage("procPlantCd", []),
  setProcPlantCd: (value) => setStorage("procPlantCd", value),
  
  // 기타
  getTelNo: () => getStorage("tel_no"),
  setTelNo: (value) => setStorage("tel_no", value),
  
  getEmail: () => getStorage("e_mail"),
  setEmail: (value) => setStorage("e_mail", value),
  
  // 자사용/업체용 구분
  getRegOption: () => getStorage("regOption", "2"),
  setRegOption: (value) => setStorage("regOption", value),
  
  // 일반 메서드
  get: getStorage,
  set: setStorage,
  remove: removeStorage,
  clear: clearStorage,
};

export default storage;

